/** A privacy-preserving share card rendered entirely in the browser. */
export interface ShareCard {
  eyebrow: string;
  headline: string;
  detail?: string;
  stamp?: string;
  /** Marks a supporter-specific card. This is deliberately client-only. */
  personal?: boolean;
}

const WIDTH = 1200;
const HEIGHT = 630;
const RED = '#EF0107';
const DEEP_RED = '#B80006';
const PAPER = '#F7F1E5';
const INK = '#171411';
const GOLD = '#C5A145';

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function fitLines(ctx: CanvasRenderingContext2D, value: string, maxWidth: number, maxLines: number): string[] {
  const words = value.trim().split(/\s+/);
  const lines: string[] = [];
  let line = '';
  let index = 0;
  for (; index < words.length; index += 1) {
    const candidate = line ? `${line} ${words[index]}` : words[index]!;
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = words[index]!;
      if (lines.length === maxLines - 1) { index += 1; break; }
    } else {
      line = candidate;
    }
  }
  let last = [line, ...words.slice(index)].filter(Boolean).join(' ');
  const clipped = index < words.length;
  while (last.length > 1 && ctx.measureText(`${last}${clipped ? '…' : ''}`).width > maxWidth) last = last.slice(0, -1).trimEnd();
  if (last) lines.push(`${last}${clipped ? '…' : ''}`);
  return lines.slice(0, maxLines);
}

export function drawShareCard(canvas: HTMLCanvasElement, card: ShareCard): void {
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas-unavailable');

  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  ctx.fillStyle = RED;
  ctx.fillRect(0, 0, 34, HEIGHT);
  ctx.fillStyle = 'rgba(23,20,17,0.08)';
  for (let y = 78; y < HEIGHT; y += 78) ctx.fillRect(34, y, WIDTH - 34, 1);

  ctx.fillStyle = RED;
  roundedRect(ctx, 82, 64, 164, 58, 4);
  ctx.fill();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 3;
  ctx.strokeRect(96, 78, 136, 30);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '900 27px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('COYG', 164, 93);

  ctx.textAlign = 'left';
  ctx.fillStyle = DEEP_RED;
  ctx.font = '800 24px Arial, sans-serif';
  ctx.fillText(card.eyebrow.toUpperCase(), 278, 96);
  ctx.fillStyle = INK;
  ctx.font = '700 72px Georgia, serif';
  ctx.textBaseline = 'alphabetic';
  fitLines(ctx, card.headline, 1000, 3).forEach((line, index) => ctx.fillText(line, 82, 238 + index * 82));

  if (card.detail) {
    ctx.fillStyle = 'rgba(23,20,17,0.72)';
    ctx.font = '500 30px Arial, sans-serif';
    fitLines(ctx, card.detail, 850, 2).forEach((line, index) => ctx.fillText(line, 82, 506 + index * 38));
  }

  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(82, 574);
  ctx.lineTo(1118, 574);
  ctx.stroke();
  ctx.fillStyle = DEEP_RED;
  ctx.font = '800 22px Arial, sans-serif';
  ctx.fillText(card.stamp ?? (card.personal ? 'MY CALL · COYG.ORG' : 'COYG.ORG'), 82, 610);
  ctx.textAlign = 'right';
  ctx.fillText('FREE · OPEN · UNOFFICIAL', 1118, 610);
}

export async function createShareCardFile(card: ShareCard): Promise<File | null> {
  if (typeof document === 'undefined' || typeof File === 'undefined') return null;
  try {
    if ('fonts' in document) await document.fonts.ready;
    const canvas = document.createElement('canvas');
    drawShareCard(canvas, card);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png', 0.94));
    return blob ? new File([blob], 'coyg-living-programme.png', { type: 'image/png' }) : null;
  } catch {
    return null;
  }
}
